import type { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useEffect, useState } from 'react';

import { safeCall } from '@/app/utils/safeCall';

export interface UsePaginationSliderProps {
  selectedIndex: number;
  currentPage: number;
  scrollSnaps: number[];
  slidesInView: number[];
  onPageChange: (index: number) => void;
}

/**
 *
 * custom hook for pagination slider with embla-carousel
 *
 * Read more: [Storybook docs: `usePaginationSlider`](https://tis-web-story-book.netlify.app/?path=/story/components-sliders--with-pagination)
 *
 * @param emblaApi EmblaCarouselType | null
 * @returns UsePaginationSliderProps
 */
export const usePaginationSlider = (
  emblaApi?: EmblaCarouselType | null,
  onReInit?: (emblaApi: EmblaCarouselType) => void
): UsePaginationSliderProps => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const [slidesInView, setSlidesInView] = useState<number[]>([]);

  const onPageChange = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index - 1);

      const autoplay = emblaApi?.plugins()?.autoplay;
      if (!autoplay) return;

      const resetOrStop =
        autoplay.options.stopOnInteraction === false
          ? autoplay.reset
          : autoplay.stop;

      resetOrStop();
    },
    [emblaApi]
  );

  const onInit = useCallback((emblaApi: EmblaCarouselType) => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
  }, []);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  const onSlidesInView = useCallback((emblaApi: EmblaCarouselType) => {
    if (!emblaApi) return;
    setSlidesInView(emblaApi.slidesInView());
  }, []);

  const handleReInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (onReInit) {
        return safeCall(onReInit, emblaApi);
      }

      onInit(emblaApi);
    },
    [onInit, onReInit]
  );

  const handleOnSelectReInit = useCallback(
    (emblaApi: EmblaCarouselType) => {
      if (onReInit) {
        return safeCall(onReInit, emblaApi);
      }

      onSelect(emblaApi);
    },
    [onReInit, onSelect]
  );

  useEffect(() => {
    if (!emblaApi) return;

    onInit(emblaApi);
    onSelect(emblaApi);
    onSlidesInView(emblaApi);
    emblaApi
      .on('reInit', handleReInit)
      .on('reInit', handleOnSelectReInit)
      .on('reInit', onSlidesInView)
      .on('slidesInView', onSlidesInView)
      .on('select', onSelect);

    return () => {
      if (!emblaApi) return;
      emblaApi
        .off('reInit', handleReInit)
        .off('reInit', handleOnSelectReInit)
        .off('reInit', onSlidesInView)
        .off('slidesInView', onSlidesInView)
        .off('select', onSelect);
    };
  }, [
    emblaApi,
    onInit,
    onSelect,
    onSlidesInView,
    handleReInit,
    handleOnSelectReInit,
  ]);

  return {
    selectedIndex,
    currentPage: selectedIndex + 1,
    scrollSnaps,
    slidesInView,
    onPageChange,
  };
};
